import {Icon} from "@client/components/Icon";
import {LineChart} from "@client/components/LineChart";
import * as dates from "@client/dates";
import * as forms from "@client/forms";
import {useAsync} from "@client/hooks";
import * as journeys from "@client/journeys";
import {useSiteContext} from "@client/site/context";
import * as style from "@client/style";
import {server} from "@reactivated";
import {produce} from "immer";
import * as React from "react";

const CHART_COLORS = [
    style.colors.chart,
    style.colors.chart2,
    style.colors.chart3,
    style.colors.chart4,
];

export const DATE_RANGES = [
    {value: "week", label: "Last week"},
    {value: "month", label: "Last month"},
    {value: "two", label: "2 months"},
    {value: "three", label: "3 months"},
    {value: "six", label: "6 months"},
    {value: "year", label: "1 year"},
    {value: "custom", label: "Custom date range"},
] as const;

type Range = (typeof DATE_RANGES)[number]["value"];

export const rangeToDates = (range: Exclude<Range, "custom">) => {
    const today = new Date();
    const dateBefore = dates.formatISO(today, {representation: "date"});

    switch (range) {
        case "week":
            return {
                range,
                date_after: dates.formatISO(dates.subDays(today, 7), {
                    representation: "date",
                }),
                date_before: dateBefore,
            };
        case "month":
            return {
                range,
                date_after: dates.formatISO(dates.subDays(today, 30), {
                    representation: "date",
                }),
                date_before: dateBefore,
            };
        case "two":
            return {
                range,
                date_after: dates.formatISO(dates.subMonths(today, 2), {
                    representation: "date",
                }),
                date_before: dateBefore,
            };
        case "three":
            return {
                range,
                date_after: dates.formatISO(dates.subMonths(today, 3), {
                    representation: "date",
                }),
                date_before: dateBefore,
            };
        case "six":
            return {
                range,
                date_after: dates.formatISO(dates.subMonths(today, 6), {
                    representation: "date",
                }),
                date_before: dateBefore,
            };
        case "year":
            return {
                range,
                date_after: dates.formatISO(dates.subYears(today, 1), {
                    representation: "date",
                }),
                date_before: dateBefore,
            };
    }

    const _exhaustiveCheck: never = range;
    throw new Error(_exhaustiveCheck);
};

export const useRangeForm = (searchParams: URLSearchParams) => {
    const defaultRange = "three";
    const defaultDates = rangeToDates(defaultRange);

    // Get initial values from URL or use defaults
    const getInitialRange = (): Range => {
        const urlRange = searchParams.get("range");
        if (urlRange != null && DATE_RANGES.some((r) => r.value === urlRange)) {
            return urlRange as Range;
        }
        // If start_date and end_date are provided in URL, infer custom range
        const urlStartDate = searchParams.get("start_date");
        const urlEndDate = searchParams.get("end_date");
        if (urlStartDate != null && urlEndDate != null) {
            return "custom";
        }
        return defaultRange;
    };

    const getInitialDates = (initialRange: Range) => {
        if (initialRange === "custom") {
            const urlStartDate = searchParams.get("start_date");
            const urlEndDate = searchParams.get("end_date");
            if (urlStartDate != null && urlEndDate != null) {
                return {
                    date_after: urlStartDate,
                    date_before: urlEndDate,
                };
            }
        }
        return initialRange === defaultRange
            ? defaultDates
            : rangeToDates(initialRange as Exclude<Range, "custom">);
    };

    const initialRange = getInitialRange();
    const initialDates = getInitialDates(initialRange);

    type State = {
        startDate: string;
        endDate: string;
        range: Range;
    };

    const [values, _setValues] = React.useState<State>(() => ({
        startDate: initialDates.date_after,
        endDate: initialDates.date_before,
        range: initialRange,
    }));

    const setValues = (newValues: Partial<State>) => {
        _setValues((prev) => {
            const updated = {...prev, ...newValues};
            const params = new URLSearchParams();

            // Only add params if they differ from defaults
            if (updated.range !== defaultRange) {
                params.set("range", updated.range);
            }

            // Add custom dates if range is custom
            if (updated.range === "custom") {
                params.set("start_date", updated.startDate);
                params.set("end_date", updated.endDate);
            }

            // Update URL without causing a page reload
            const newUrl = params.toString()
                ? `${window.location.pathname}?${params.toString()}`
                : window.location.pathname;
            window.history.replaceState({}, "", newUrl);

            return updated;
        });
    };

    const rangeForm = forms.useForm(
        {
            fields: {
                range: {
                    kind: "select",
                    nullable: false,
                    widget: "select",
                    options: DATE_RANGES.map((item) => [item.value, item.label]),
                    required: true,
                },
                start_date: {
                    kind: "date",
                    nullable: false,
                    required: true,
                },
                end_date: {
                    kind: "date",
                    nullable: false,
                    required: true,
                },
            },
        },
        {
            value: {
                range: values.range,
                start_date: values.startDate,
                end_date: values.endDate,
            },
            onChange: (formValues) => {
                // If range changed to non-custom, calculate new dates
                if (
                    formValues.range !== "custom" &&
                    formValues.range !== values.range
                ) {
                    const {date_after, date_before} = rangeToDates(formValues.range);
                    setValues({
                        startDate: date_after,
                        endDate: date_before,
                        range: formValues.range,
                    });
                } else {
                    setValues({
                        startDate: formValues.start_date,
                        endDate: formValues.end_date,
                        range: formValues.range,
                    });
                }
            },
        },
    );

    return rangeForm;
};

const MEASUREMENT_LABELS: Record<
    server.core.rpc.Profile.output["user"]["get_measurements"][number],
    string
> = {
    weight: "Weight",
    waist: "Waist",
    navel: "Navel",
    body_fat: "Body Fat",
    hips: "Hips",
    neck: "Neck",
    thigh: "Thigh",
    bicep: "Bicep",
    chest: "Chest",
};

export function Measurements(props: {
    profile: server.core.rpc.Profile.output;
    rangeForm: ReturnType<typeof useRangeForm>;
}) {
    const {profile, rangeForm} = props;
    const [metric, setMetric] = React.useState(profile.user.get_measurements[0]);
    const form = forms.useField(
        {
            kind: "select",
            nullable: false,
            widget: "select",
            options: profile.user.get_measurements.map((m) => [
                m,
                MEASUREMENT_LABELS[m],
            ]),
            required: true,
        },
        {value: metric, onChange: setMetric},
    );
    const result = useAsync(
        () =>
            server.journal.reports.reports({
                exercise_types: [metric],
                user_id: props.profile.user.id,
                stat: "ESTIMATED_1RM",
                start_date: rangeForm.values.start_date,
                end_date: rangeForm.values.end_date,
            }),
        [rangeForm.values.start_date, rangeForm.values.end_date, metric],
    );

    return (
        <div className="flex h-full flex-1 flex-col space-y-2">
            <forms.Widget field={form} />
            <div className="relative flex-1">
                <div className="absolute inset-0 rounded-xl bg-white py-2 pr-4 pl-1">
                    {result.value?.type != "success" ? (
                        <div>Loading...</div>
                    ) : (
                        <LineChart
                            lines={result.value.data.lines.map((line, index) => ({
                                ...line,
                                color: CHART_COLORS[index],
                            }))}
                        />
                    )}
                </div>
            </div>
            <forms.Widget field={rangeForm.fields.range} />
            <div className="flex gap-2">
                <forms.Widget
                    field={{
                        ...rangeForm.fields.start_date,
                        readOnly: rangeForm.values.range != "custom",
                    }}
                />
                <forms.Widget
                    field={{
                        ...rangeForm.fields.end_date,
                        readOnly: rangeForm.values.range != "custom",
                    }}
                />
            </div>
        </div>
    );
}

export function Exercises(props: {
    profile: server.core.rpc.Profile.output;
    rangeForm: ReturnType<typeof useRangeForm>;
}) {
    const [exercises, setExercises] = React.useState<
        Array<server.journal.workouts.ExerciseType.output | null>
    >([null, null, null, null]);

    const [stat, setStat] = React.useState<
        | "VOLUME"
        | "ESTIMATED_1RM"
        | "ESTIMATED_1RM_RATIO"
        | "WORKOUT_MAX"
        | "WORKOUT_MAX_RATIO"
        | "REPS"
    >("WORKOUT_MAX");
    const [metric, setMetric] = React.useState<"weight" | null>(null);
    const [weight, setWeight] = React.useState(false);

    const exercisesDialog = journeys.useExercisesDialog();

    const {rangeForm} = props;
    const statForm = forms.useForm(
        {
            fields: {
                stat: {
                    kind: "select",
                    nullable: false,
                    widget: "select",
                    label: "Exercise stat",
                    options: [
                        ["WORKOUT_MAX", "Workout Max"],
                        ["WORKOUT_MAX_RATIO", "Workout Max / Body Weight"],
                        ["ESTIMATED_1RM", "Estimated 1 Rep Max"],
                        ["ESTIMATED_1RM_RATIO", "Estimated 1 Rep Max / Body Weight"],
                        ["VOLUME", "Total Volume"],
                        ["REPS", "Total Reps"],
                    ],
                    required: true,
                },
                metric: {
                    kind: "select",
                    nullable: true,
                    label: "Body metric",
                    options: [["weight", "Weight"]],
                    required: false,
                },
                weight: {
                    kind: "boolean",
                    required: true,
                },
            },
        },
        {
            value: {stat, metric, weight},
            onChange: (values) => {
                setStat(values.stat);
                setMetric(values.metric);
                setWeight(values.weight);
            },
        },
    );

    const result = useAsync(
        () =>
            server.journal.reports.reports({
                exercise_types: exercises
                    .filter((item) => item != null)
                    .map((exercise) => ({uuid: exercise.uuid})),
                user_id: props.profile.user.id,
                stat: statForm.values.stat,
                start_date: rangeForm.values.start_date,
                end_date: rangeForm.values.end_date,
            }),
        [
            exercises,
            stat,
            rangeForm.values.start_date,
            rangeForm.values.end_date,
            metric,
        ],
    );

    const {realm} = useSiteContext();

    return (
        <div className="flex h-full flex-1 flex-col space-y-2">
            <div className="grid grid-cols-2 gap-2">
                {exercises.map((exercise, index) => {
                    if (exercise == null) {
                        return (
                            <button
                                key={index}
                                type="button"
                                className="flex h-8 items-center gap-2 rounded-sm bg-white px-2 py-1 text-xs font-semibold text-gray-500 shadow-xs ring-1 ring-gray-300 ring-inset hover:bg-gray-50"
                                onClick={async () => {
                                    const result = await exercisesDialog({
                                        filter: (exerciseType) =>
                                            (exerciseType.category == "WEIGHT" ||
                                                exerciseType.category ==
                                                    "BODYWEIGHT") &&
                                            (realm == "site" ||
                                                exerciseType.latest_instances.length >
                                                    0) &&
                                            exercises.find(
                                                (m) => m?.uuid == exerciseType.uuid,
                                            ) == null,
                                        disableCreate: true,
                                    });

                                    if (result != null) {
                                        const exerciseType = result[0];
                                        setExercises(
                                            produce(exercises, (draftState) => {
                                                const firstEmpty = draftState.findIndex(
                                                    (m) => m == null,
                                                );
                                                draftState[firstEmpty] = exerciseType;
                                            }),
                                        );
                                    }
                                }}
                            >
                                <svg
                                    className="size-1.5"
                                    style={{fill: "#DDD"}}
                                    viewBox="0 0 6 6"
                                    aria-hidden="true"
                                >
                                    <circle cx={3} cy={3} r={3} />
                                </svg>
                                <div className="flex-1 text-left">Select exercise</div>
                            </button>
                        );
                    }
                    return (
                        <button
                            key={index}
                            type="button"
                            onClick={() => {
                                setExercises(
                                    produce(exercises, (draftState) => {
                                        draftState[index] = null;
                                    }),
                                );
                            }}
                            className="flex h-8 items-center gap-2 rounded-sm bg-white px-2 py-1 text-xs font-semibold text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset hover:bg-gray-50"
                        >
                            <svg
                                className="size-1.5"
                                style={{fill: CHART_COLORS[index].point.border}}
                                viewBox="0 0 6 6"
                                aria-hidden="true"
                            >
                                <circle cx={3} cy={3} r={3} />
                            </svg>
                            <div className="flex-1 truncate text-left">
                                {exercise.name}
                            </div>
                            <Icon icon="0822-cross2" large="small"></Icon>
                        </button>
                    );
                })}
            </div>
            <div className="flex gap-2 *:flex-1">
                <forms.Widget field={statForm.fields.stat} />
                {/*
                    <label className="flex items-center gap-2 font-semibold text-gray-700 justify-center">
                            Show weight
                        <forms.Widget field={statForm.fields.weight} />
                    </label>
                */}
            </div>
            <div className="relative flex-1">
                {exercisesDialog.dialog}
                <div className="absolute inset-0 rounded-xl bg-white py-2 pr-4 pl-1">
                    {result.value?.type != "success" ? (
                        <div>Loading...</div>
                    ) : (
                        <LineChart
                            lines={result.value.data.lines.map((line, index) => ({
                                ...line,
                                color: CHART_COLORS[index],
                            }))}
                            right={
                                weight == true ? result.value.data.weight : undefined
                            }
                        />
                    )}
                </div>
            </div>
            <forms.Widget field={rangeForm.fields.range} />
            <div className="flex gap-2">
                <forms.Widget
                    field={{
                        ...rangeForm.fields.start_date,
                        readOnly: rangeForm.values.range != "custom",
                    }}
                />
                <forms.Widget
                    field={{
                        ...rangeForm.fields.end_date,
                        readOnly: rangeForm.values.range != "custom",
                    }}
                />
            </div>
        </div>
    );
}
